import React, { useMemo, useState } from 'react';
import { Redirect } from 'react-router';
import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Container, List, Typography } from '@mui/material';
import { ROUTING_CONFIG } from '@uplift/constants';
import { useIsCollaborativePhysician, useL10n } from '@uplift/hooks';
import { Loading } from '@uplift/pages';
import { ProviderLayout, ProviderLayoutPageHeader } from '@uplift/ui-v2';

import { SuperviseeListItem } from './components/SuperviseeListItem';
import { ALL_STATES, SuperviseesFilters } from './components/SuperviseesFilters';
import { SuperviseesByState, useSuperviseesByState } from './supervisees.hook';
import { useCollaborativeStateDetailsQuery } from '@uplift/queries';


const StateAccordion: React.VFC<SuperviseesByState> = ({ state, rows }) => {
  const { data: collaborativeStateDetails } = useCollaborativeStateDetailsQuery(state.id);

  return (
    <Accordion elevation={0}>
      <AccordionSummary expandIcon={<ExpandMore />}>
        <Typography sx={{ fontWeight: 500 }}>{state.fullName}</Typography>
        {collaborativeStateDetails.physicianSeatCapacity != null && (
          <Typography variant="body2" color="text.secondary" sx={{ ml: 'auto', alignSelf: 'center' }}>
            Seat capacity: {rows.length}/{collaborativeStateDetails.physicianSeatCapacity} seats filled
          </Typography>
        )}
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        <List sx={{ padding: 0, paddingLeft: 2 }}>
          {rows.map(supervisee => (
            <SuperviseeListItem key={supervisee.providerId} supervisee={supervisee} />
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
};


export const SuperviseesPage: React.VFC = () => {
  const l10n = useL10n();
  const { supervisees, groupedByState, isLoading } = useSuperviseesByState();
  const [ searchText, setSearchText ] = useState('');
  const [ selectedStateId, setSelectedStateId ] = useState<string>(ALL_STATES);

  const filteredGroups = useMemo(() => {
    const search = searchText.trim().toLowerCase();

    return groupedByState
      .filter(group => selectedStateId === ALL_STATES || group.state.id === selectedStateId)
      .map(group => ({
        ...group,
        rows: search
          ? group.rows.filter(row => `${row.user.firstName} ${row.user.lastName}`.toLowerCase().includes(search))
          : group.rows,
      }))
      .filter(group => group.rows.length > 0);
  }, [ groupedByState, searchText, selectedStateId ]);

  const isCollaborativePhysician = useIsCollaborativePhysician();

  if (isLoading) {
    return <Loading />;
  }

  if (!isCollaborativePhysician) {
    return <Redirect to={ROUTING_CONFIG.main} />;
  }

  return (
    <ProviderLayout>
      <ProviderLayoutPageHeader title={l10n.pages.supervisees} />
      <Container sx={{ py: 4 }}>
        {supervisees.length === 0 ? (
          <Typography variant="h5" color="text.secondary" textAlign="center" sx={{ p: 3 }}>
            {l10n.supervisees.emptyState}
          </Typography>
        ) : (
          <>
            <SuperviseesFilters
              searchText={searchText}
              onSearchTextChange={setSearchText}
              selectedStateId={selectedStateId}
              onSelectedStateIdChange={setSelectedStateId}
              groups={groupedByState}
            />
            {filteredGroups.map(group => (
              <StateAccordion key={group.state.id} state={group.state} rows={group.rows} />
            ))}
          </>
        )}
      </Container>
    </ProviderLayout>
  );
};
